import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { formatTRY } from "@/lib/format";
import { toast } from "sonner";
import {
  Mail as MailIcon, Plus, Trash2, RefreshCw, Loader2, ChevronDown, Star, CheckCircle2, PlusCircle, ShieldCheck,
} from "lucide-react";

const PROVIDERS = [
  { key: "gmail", label: "Gmail" },
  { key: "outlook", label: "Outlook / Hotmail" },
  { key: "icloud", label: "iCloud" },
];

const GUIDES = {
  gmail: [
    "myaccount.google.com adresine girin → Güvenlik.",
    "\"2 Adımlı Doğrulama\"yı açın (zorunlu).",
    "Arama kutusuna \"Uygulama şifreleri\" yazıp açın.",
    "Uygulama adı yazın (ör. İstasyonFinans) → Oluştur.",
    "Çıkan 16 haneli şifreyi buraya yapıştırın (boşluksuz).",
  ],
  outlook: [
    "\"Microsoft ile Bağlan\" düğmesine basın.",
    "Microsoft / Hotmail hesabınızla giriş yapın.",
    "Microsoft tarafından istenen e-posta erişim iznini onaylayın.",
    "İşlem tamamlanınca otomatik olarak bu sayfaya dönersiniz.",
  ],
  icloud: [
    "appleid.apple.com adresinde oturum açın.",
    "\"Oturum Açma ve Güvenlik\" → \"Uygulamaya Özel Parolalar\".",
    "\"+\" ile yeni parola oluşturun (ör. İstasyonFinans).",
    "Verilen parolayı buraya yapıştırın. E-posta olarak iCloud adresinizi girin.",
  ],
};

export default function MailPage() {
  const [accounts, setAccounts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({ provider: "gmail", email: "", app_password: "" });
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [guideOpen, setGuideOpen] = useState(true);
  const [onlyImportant, setOnlyImportant] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([api.get("/mail/accounts"), api.get("/mail/messages")]);
      setAccounts(a.data || []);
      setMessages(m.data || []);
    } catch (e) {}
    setLoading(false);
  };
  useEffect(() => {
    load();

    const params = new URLSearchParams(window.location.search);
    const microsoft = params.get("microsoft");

    if (microsoft === "connected") {
      toast.success("Microsoft / Hotmail hesabı bağlandı");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (microsoft === "error") {
      toast.error("Microsoft hesabı bağlanamadı");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connectMicrosoft = async () => {
    setConnecting(true);

    try {
      const r = await api.get("/mail/microsoft/connect");

      if (!r.data?.url) {
        throw new Error("Microsoft OAuth URL alınamadı");
      }

      window.location.href = r.data.url;
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
        "Microsoft bağlantısı başlatılamadı"
      );
      setConnecting(false);
    }
  };

  const connect = async () => {
    if (!form.email || !form.app_password) { toast.error("E-posta ve uygulama şifresi gerekli"); return; }
    setConnecting(true);
    try {
      await api.post("/mail/accounts", form);
      toast.success("Hesap bağlandı");
      setForm({ provider: form.provider, email: "", app_password: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bağlanamadı");
    } finally { setConnecting(false); }
  };

  const removeAcc = async (id) => {
    if (!window.confirm("Hesap bağlantısı kaldırılsın mı?")) return;
    try { await api.delete(`/mail/accounts/${id}`); toast.success("Kaldırıldı"); load(); }
    catch (e) { toast.error("Kaldırılamadı"); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/mail/refresh");
      toast.success(`${r.data.new} yeni e-posta işlendi`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kontrol edilemedi"); }
    finally { setRefreshing(false); }
  };

  const addToPlan = async (m) => {
    try {
      await api.post(`/mail/messages/${m.id}/add-to-plan`, { due_date: m.due_date, amount: m.amount, note: m.subject });
      toast.success("Ödeme planına eklendi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eklenemedi"); }
  };

  const shown = onlyImportant ? messages.filter((m) => m.important) : messages;

  return (
    <div className="p-5 md:p-8 max-w-4xl" data-testid="mail-page">
      <PageHeader
        title="E-postalar"
        subtitle="Gmail, Outlook/Hotmail ve iCloud hesaplarınızı bağlayın; yapay zeka önemli faturaları süzüp özetlesin, ödemeleri tek tıkla plana ekleyin."
        testid="mail-header"
        action={
          <button onClick={refresh} disabled={refreshing || accounts.length === 0} data-testid="mail-refresh-button" className="flex items-center gap-2 bg-[#E65100] hover:bg-[#FF6D00] disabled:opacity-50 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Mailleri Kontrol Et
          </button>
        }
      />

      {/* Connected accounts */}
      <div className="card-surface p-5 mb-5 fade-up">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><MailIcon className="w-4 h-4 text-[#E65100]" /> Bağlı Hesaplar</div>
        {accounts.length === 0 ? (
          <p className="text-xs text-[#94A3B8] mb-4">Henüz hesap bağlanmadı. Aşağıdan ekleyin.</p>
        ) : (
          <div className="space-y-2 mb-4">
            {accounts.map((a) => (
              <div key={a.id} className="flex items-center gap-3 bg-[#111620] rounded-xl px-3 py-2.5" data-testid="mail-account-item">
                <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#1B2430] text-[#CBD5E1]">{a.provider}</span>
                <span className="flex-1 text-sm truncate">{a.email}</span>
                <button onClick={() => removeAcc(a.id)} className="w-8 h-8 rounded-lg hover:bg-[#EF4444]/20 flex items-center justify-center text-[#94A3B8] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}

        {/* Add form */}
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr_auto] gap-2.5 items-end">
          <div>
            <label className="block text-xs text-[#94A3B8] mb-1.5">Sağlayıcı</label>
            <select data-testid="mail-provider-select" value={form.provider} onChange={(e) => { setForm({ ...form, provider: e.target.value }); setGuideOpen(true); }} className={inp}>
              {PROVIDERS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          {form.provider === "outlook" ? (
            <div className="sm:col-span-3">
              <button
                onClick={connectMicrosoft}
                disabled={connecting}
                data-testid="mail-microsoft-connect-button"
                className="w-full px-4 py-2.5 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2 justify-center transition-colors"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                Microsoft ile Bağlan
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs text-[#94A3B8] mb-1.5">
                  E-posta
                </label>
                <input
                  data-testid="mail-email-input"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  className={inp}
                  placeholder={
                    form.provider === "icloud"
                      ? "ornek@icloud.com"
                      : "ornek@gmail.com"
                  }
                />
              </div>

              <div>
                <label className="block text-xs text-[#94A3B8] mb-1.5">
                  Uygulama Şifresi
                </label>
                <input
                  data-testid="mail-password-input"
                  value={form.app_password}
                  onChange={(e) =>
                    setForm({ ...form, app_password: e.target.value })
                  }
                  className={inp}
                  placeholder="Uygulama şifresi"
                />
              </div>

              <button
                onClick={connect}
                disabled={connecting}
                data-testid="mail-connect-button"
                className="px-4 py-2.5 rounded-xl bg-[#161D2A] hover:bg-[#1D2637] border border-[#253043] text-sm font-semibold flex items-center gap-2 justify-center transition-colors"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Bağla
              </button>
            </>
          )}
        </div>

        {/* Guide */}
        <div className="mt-4 rounded-xl bg-[#111620] border border-[#253043] overflow-hidden">
          <button onClick={() => setGuideOpen(!guideOpen)} className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-[#CBD5E1]">
            <ShieldCheck className="w-4 h-4 text-[#10B981]" />{" "}
            {form.provider === "outlook"
              ? "Microsoft hesabı nasıl bağlanır?"
              : `${PROVIDERS.find((p) => p.key === form.provider)?.label} için uygulama şifresi nasıl alınır?`}
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${guideOpen ? "rotate-180" : ""}`} />
          </button>
          {guideOpen && (
            <ol className="px-5 pb-4 pt-1 space-y-1.5 text-xs text-[#94A3B8] list-decimal list-inside">
              {GUIDES[form.provider].map((g, i) => <li key={i}>{g}</li>)}
            </ol>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-sm">Özetlenen E-postalar</h3>
        <button onClick={() => setOnlyImportant(!onlyImportant)} data-testid="mail-filter-toggle" className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${onlyImportant ? "bg-[#E65100] border-[#E65100] text-white" : "bg-transparent border-[#253043] text-[#94A3B8]"}`}>
          {onlyImportant ? "Sadece Önemli" : "Tümü"}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#E65100]" /></div>
      ) : shown.length === 0 ? (
        <div className="card-surface p-10 text-center text-[#94A3B8] text-sm">
          {accounts.length === 0 ? "Önce bir e-posta hesabı bağlayın." : "Henüz özetlenmiş e-posta yok. \"Mailleri Kontrol Et\"e basın."}
        </div>
      ) : (
        <div className="space-y-2.5">
          {shown.map((m) => (
            <div key={m.id} className="card-surface p-4 fade-up" data-testid="mail-message-item">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.important ? "bg-[#F59E0B]/20" : "bg-[#1B2430]"}`}>
                  {m.important ? <Star className="w-4 h-4 text-[#F59E0B]" /> : <MailIcon className="w-4 h-4 text-[#64748B]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate max-w-[60%]">{m.subject || "(konu yok)"}</span>
                    {m.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1B2430] text-[#CBD5E1]">{m.category}</span>}
                  </div>
                  <div className="text-[11px] text-[#64748B] truncate">{m.from} · {m.date}</div>
                  {m.summary && <p className="text-xs text-[#CBD5E1] mt-1.5">{m.summary}</p>}
                  {(m.amount || m.due_date) && (
                    <div className="flex items-center gap-3 mt-2">
                      {m.amount && <span className="font-mono-num text-sm font-semibold text-[#E65100]">{formatTRY(m.amount)}</span>}
                      {m.due_date && <span className="text-[11px] text-[#94A3B8]">Vade: {m.due_date.split("-").reverse().join(".")}</span>}
                      {m.amount && m.due_date && (m.added_to_plan ? (
                        <span className="flex items-center gap-1 text-[11px] text-[#34D399]"><CheckCircle2 className="w-3.5 h-3.5" /> Plana eklendi</span>
                      ) : (
                        <button onClick={() => addToPlan(m)} data-testid="mail-add-to-plan-button" className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-lg bg-[#E65100] hover:bg-[#FF6D00] text-white transition-colors">
                          <PlusCircle className="w-3.5 h-3.5" /> Ödeme Planına Ekle
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl bg-[#111620] border border-[#253043] focus:border-[#E65100] outline-none transition-colors text-sm";
